/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useCallback, useMemo, useContext } from 'react';
import { ControlledDateTimePickerContext } from '../../ControlledDateTimePickerCTX.js';
import { prependStringWithPlaceHolders } from '../../utils/stringHelpers.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
interface UseTimeInputsR {
  autoFocusHourInput: boolean;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  handleChangeHours: DSControlledDateTimePickerInternalsT.HandleOnChangeFunc;
  onHoursChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  handleChangeMinutes: DSControlledDateTimePickerInternalsT.HandleOnChangeFunc;
  onMinutesChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  handleChangeMeridiem: DSControlledDateTimePickerInternalsT.HandleOnChangeFunc;
  onMeridiemChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onHoursFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onMinutesFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onMeridiemFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onHoursBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onMinutesBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onMeridiemBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onHoursKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  onMinutesKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  onMeridiemKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
}
export const useTimeInputs = (): UseTimeInputsR => {
  const {
    props: { disabled, readOnly, applyAriaDisabled },
    hours,
    minutes,
    meridiem,
    withAnyRightController,
    withAnyPicker,
    withClearBtn,
    isWithDateInputs,
    autoFocusHourInput,
    handleChangeHours,
    handleChangeMinutes,
    handleChangeMeridiem,
    minutesInterval,
    trackFocusFirstSegment,
    trackFocusLastSegment,
    trackFocusHourInput,
    trackFocusMinuteInput,
    trackFocusMeridiemInput,
    trackFocusClearBtn,
    trackFocusPicker,
    trackFocusYearInput,
  } = useContext(ControlledDateTimePickerContext);

  const shouldPreventValueChanges = disabled || applyAriaDisabled || readOnly;

  const onHomeKeyDown = useCallback(() => {
    trackFocusFirstSegment();
  }, [trackFocusFirstSegment]);
  const onEndKeyDown = useCallback(() => {
    trackFocusLastSegment();
  }, [trackFocusLastSegment]);

  const onHoursFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc = useCallback(() => {
    trackFocusHourInput();
  }, [trackFocusHourInput]);
  const onHoursChange: DSControlledDateTimePickerInternalsT.OnChangeFunc = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (shouldPreventValueChanges) return;
      let newHours = e?.target?.value;
      // converts hours 2~9 to 07~09
      if (Number.parseInt(newHours, 10) > 1) newHours = prependStringWithPlaceHolders(newHours, 2);
      handleChangeHours(newHours, e);
      // auto-advance functionality:
      if (newHours.length === 2) trackFocusMinuteInput();
    },
    [handleChangeHours, shouldPreventValueChanges, trackFocusMinuteInput],
  );
  const onHoursKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key, ctrlKey, metaKey } = e;
      const hoursStringConvertedToInteger = Number.parseInt(hours, 10);

      if (['Home', 'End', 'ArrowUp', 'ArrowDown', ':'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', ':'].includes(key)) e.stopPropagation();
      /* ***** CODE TO HANDLE FOCUS CHANGES ***** */
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown();
      // focus last fragment on fill with now
      if ((ctrlKey || metaKey) && key === ';') trackFocusLastSegment();

      if (shouldPreventValueChanges) return;

      /* ***** CODE TO HANDLE VALUE CHANGES ***** */
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        const hoursInteger = Number.isNaN(hoursStringConvertedToInteger) ? 0 : hoursStringConvertedToInteger;
        let newHours;
        if (key === 'ArrowUp') newHours = hoursInteger + 1 <= 12 ? hoursInteger + 1 : hoursInteger;
        else newHours = hoursInteger - 1 > 1 ? hoursInteger - 1 : 1;
        newHours = prependStringWithPlaceHolders(`${newHours}`, 2);
        handleChangeHours(newHours, e);
      }
      if (!Number.isNaN(hoursStringConvertedToInteger) && key === ':') {
        let newHours = prependStringWithPlaceHolders(`${hoursStringConvertedToInteger}`, 2);
        // 12-hours format: 00 is not allowed as a value for hours
        if (newHours === '00') newHours = '01';
        handleChangeHours(newHours, e);
        // auto-advance functionality:
        if (newHours.length === 2) trackFocusMinuteInput();
      }

      // regressive backspace functionality
      if (
        key === 'Backspace' &&
        e.target === e.currentTarget &&
        isWithDateInputs &&
        (e.target as HTMLInputElement).selectionStart === 0 &&
        (e.target as HTMLInputElement).selectionEnd === 0
      ) {
        e.preventDefault();

        trackFocusYearInput();
      }
    },
    [
      hours,
      onHomeKeyDown,
      onEndKeyDown,
      trackFocusLastSegment,
      shouldPreventValueChanges,
      isWithDateInputs,
      handleChangeHours,
      trackFocusMinuteInput,
      trackFocusYearInput,
    ],
  );
  const onHoursBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newHours = e?.target?.value;
      if (newHours === '0') handleChangeHours('01', e, { isAutomaticFillTrigger: true });
      else if (newHours.length > 0 && newHours.length < 2)
        handleChangeHours(prependStringWithPlaceHolders(newHours, 2), e, {
          isAutomaticFillTrigger: true,
        });
    },
    [handleChangeHours],
  );

  const onMinutesFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc = useCallback(() => {
    trackFocusMinuteInput();
  }, [trackFocusMinuteInput]);
  const onMinutesChange: DSControlledDateTimePickerInternalsT.OnChangeFunc = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (shouldPreventValueChanges) return;
      let newMinutes = e?.target?.value;
      // converts minutes 7~9 to 07~09
      if (Number.parseInt(newMinutes, 10) > 6) newMinutes = prependStringWithPlaceHolders(newMinutes, 2);
      handleChangeMinutes(newMinutes, e);

      // auto-advance functionality:
      if (newMinutes.length === 2) trackFocusMeridiemInput();
    },
    [handleChangeMinutes, shouldPreventValueChanges, trackFocusMeridiemInput],
  );
  const onMinutesKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key, ctrlKey, metaKey } = e;
      const minutesStringConvertedToInteger = Number.parseInt(minutes, 10);
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', ' '].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', ' '].includes(key)) e.stopPropagation();

      /* ***** CODE TO HANDLE FOCUS CHANGES ***** */
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown();
      // focus last fragment on fill with now
      if ((ctrlKey || metaKey) && key === ';') trackFocusLastSegment();

      if (shouldPreventValueChanges) return;

      /* ***** CODE TO HANDLE VALUE CHANGES ***** */
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        const minutesInteger = Number.isNaN(minutesStringConvertedToInteger) ? 0 : minutesStringConvertedToInteger;
        let newMinutes;
        if (key === 'ArrowUp')
          newMinutes = minutesInteger + minutesInterval <= 59 ? minutesInteger + minutesInterval : minutesInteger;
        else newMinutes = minutesInteger - minutesInterval > 0 ? minutesInteger - minutesInterval : 0;
        newMinutes = prependStringWithPlaceHolders(`${newMinutes}`, 2);
        handleChangeMinutes(newMinutes, e);
      }
      if (!Number.isNaN(minutesStringConvertedToInteger) && key === ' ') {
        const newMinutes = prependStringWithPlaceHolders(`${minutesStringConvertedToInteger}`, 2);
        handleChangeMinutes(newMinutes, e);
        // auto-advance functionality:
        if (newMinutes.length === 2) trackFocusMeridiemInput();
      }

      // regressive backspace functionality
      if (
        key === 'Backspace' &&
        e.target === e.currentTarget &&
        (e.target as HTMLInputElement).selectionStart === 0 &&
        (e.target as HTMLInputElement).selectionEnd === 0
      ) {
        e.preventDefault();

        trackFocusHourInput();
      }
    },
    [
      minutes,
      onHomeKeyDown,
      onEndKeyDown,
      trackFocusLastSegment,
      shouldPreventValueChanges,
      minutesInterval,
      handleChangeMinutes,
      trackFocusMeridiemInput,
      trackFocusHourInput,
    ],
  );
  const onMinutesBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newMinutes = e?.target?.value;
      if (newMinutes.length > 0 && newMinutes.length < 2)
        handleChangeMinutes(prependStringWithPlaceHolders(newMinutes, 2), e, {
          isAutomaticFillTrigger: true,
        });
    },
    [handleChangeMinutes],
  );

  const onMeridiemFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc = useCallback(() => {
    trackFocusMeridiemInput();
  }, [trackFocusMeridiemInput]);
  const onMeridiemChange: DSControlledDateTimePickerInternalsT.OnChangeFunc = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (shouldPreventValueChanges) return;
      let newMeridiem = e?.target?.value?.toUpperCase?.();
      // if new value is length 1,
      //   if previous value was empty, auto-complete
      //   if previous value was not empty, set to empty (regressive backspace)
      if (newMeridiem.length === 1)
        if (meridiem === '') newMeridiem = `${newMeridiem}M`;
        else newMeridiem = '';
      handleChangeMeridiem(newMeridiem, e);
      // auto-advance functionality:
      if (newMeridiem.length === 2 && withAnyRightController) {
        if (withClearBtn) trackFocusClearBtn();
        else if (withAnyPicker) trackFocusPicker();
      }
    },
    [
      shouldPreventValueChanges,
      meridiem,
      handleChangeMeridiem,
      withAnyRightController,
      withClearBtn,
      trackFocusClearBtn,
      withAnyPicker,
      trackFocusPicker,
    ],
  );
  const onMeridiemKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key, ctrlKey, metaKey } = e;

      if (['Home', 'End', 'ArrowUp', 'ArrowDown'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown'].includes(key)) e.stopPropagation();

      /* ***** CODE TO HANDLE FOCUS CHANGES ***** */
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown();
      // focus last fragment on fill with now
      if ((ctrlKey || metaKey) && key === ';') trackFocusLastSegment();

      if (shouldPreventValueChanges) return;

      /* ***** CODE TO HANDLE VALUE CHANGES ***** */
      if (key === 'ArrowUp' && meridiem !== 'AM') handleChangeMeridiem('AM', e);
      if (key === 'ArrowDown' && meridiem !== 'PM') handleChangeMeridiem('PM', e);
      // regressive backspace functionality
      if (key === 'Backspace') {
        if (
          e.target === e.currentTarget &&
          (e.target as HTMLInputElement).selectionStart === 0 &&
          (e.target as HTMLInputElement).selectionEnd === 0
        ) {
          e.preventDefault();
          trackFocusMinuteInput();
        } else if (meridiem?.length === 2) handleChangeMeridiem('', e);
      }
    },
    [
      onHomeKeyDown,
      onEndKeyDown,
      trackFocusLastSegment,
      shouldPreventValueChanges,
      meridiem,
      handleChangeMeridiem,
      trackFocusMinuteInput,
    ],
  );
  const onMeridiemBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const newMeridiem = e?.target?.value?.toUpperCase?.();

      if (newMeridiem.length === 1)
        handleChangeMeridiem(`${newMeridiem}M`, e, {
          isAutomaticFillTrigger: true,
        });
    },
    [handleChangeMeridiem],
  );

  return useMemo(
    () => ({
      autoFocusHourInput,
      disabled,
      applyAriaDisabled,
      handleChangeHours,
      onHoursFocus,
      onHoursChange,
      handleChangeMinutes,
      onMinutesFocus,
      onMinutesChange,
      handleChangeMeridiem,
      onMeridiemFocus,
      onMeridiemChange,
      onHoursBlur,
      onMinutesBlur,
      onMeridiemBlur,
      onHoursKeyDown,
      onMinutesKeyDown,
      onMeridiemKeyDown,
    }),
    [
      autoFocusHourInput,
      disabled,
      applyAriaDisabled,
      handleChangeHours,
      onHoursFocus,
      onHoursChange,
      handleChangeMinutes,
      onMinutesFocus,
      onMinutesChange,
      handleChangeMeridiem,
      onMeridiemFocus,
      onMeridiemChange,
      onHoursBlur,
      onMinutesBlur,
      onMeridiemBlur,
      onHoursKeyDown,
      onMinutesKeyDown,
      onMeridiemKeyDown,
    ],
  );
};
